'use client'

import { create } from 'zustand'
import type { IPendingAgentAttachmentPayload } from '@/features/agents/agent-pending-attachment-store'

interface IGlobalTaskComposerStore {
    isOpen: boolean
    submitSignal: number
    /** When true, the composer should submit without showing any UI. */
    silentAutoSubmit: boolean
    /** When set, the new task will be assigned to this project. */
    projectUuid: string | null
    projectName: string | null
    /**
     * Silent auto-submit dispatches (`dispatchNewTaskIntent({ autoSubmit: true })`) stage their
     * prompt/attachments here so the headless create path remains separate from visible drafts.
     */
    silentPrompt: string
    silentAttachments: IPendingAgentAttachmentPayload[]
    open: () => void
    close: () => void
    setOpen: (isOpen: boolean) => void
    requestSubmit: () => void
    openSilent: () => void
    openForProject: (projectUuid: string, projectName: string) => void
    queueSilentPrompt: (prompt: string) => void
    queueSilentAttachment: (attachment: IPendingAgentAttachmentPayload) => void
    /** Consume and clear the accumulated silent prompt/attachments in one shot. */
    consumeSilentIntent: () => { prompt: string; attachments: IPendingAgentAttachmentPayload[] }
}

export const useGlobalTaskComposerStore = create<IGlobalTaskComposerStore>()((set, get) => ({
    isOpen: false,
    submitSignal: 0,
    silentAutoSubmit: false,
    projectUuid: null,
    projectName: null,
    silentPrompt: '',
    silentAttachments: [],
    open: () => set({ isOpen: true, silentAutoSubmit: false, projectUuid: null, projectName: null }),
    close: () => set({ isOpen: false, silentAutoSubmit: false }),
    setOpen: (isOpen) => set(isOpen ? { isOpen } : { isOpen, silentAutoSubmit: false }),
    requestSubmit: () =>
        set((state) => ({
            submitSignal: state.submitSignal + 1,
        })),
    // Resets any stale project assignment too — a silent auto-submit never targets a project, so
    // it must never inherit one left over from an earlier, unrelated `openForProject` call.
    openSilent: () => set({ isOpen: true, silentAutoSubmit: true, projectUuid: null, projectName: null }),
    openForProject: (projectUuid, projectName) =>
        set({ isOpen: true, silentAutoSubmit: false, projectUuid, projectName }),
    queueSilentPrompt: (prompt) => set({ silentPrompt: prompt }),
    queueSilentAttachment: (attachment) =>
        set((state) => ({ silentAttachments: [...state.silentAttachments, attachment] })),
    consumeSilentIntent: () => {
        const { silentPrompt, silentAttachments } = get()
        set({ silentPrompt: '', silentAttachments: [] })
        return { prompt: silentPrompt, attachments: silentAttachments }
    },
}))

// Explicit non-hook alias for utility/non-react usage via getState/setState.
export const globalTaskComposerStore = useGlobalTaskComposerStore
